import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, LoaderCircle, MailCheck } from "lucide-react";
import { Logo, EASE } from "@/components/landing/Reveal";
import { API } from "@/context/AuthContext";

function Shell({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50" data-testid="password-reset-shell">
      <header className="border-b border-slate-100 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <Link to="/"><Logo compact /></Link>
          <Link to="/login" className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-bleu" data-testid="reset-back-login">
            <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
          </Link>
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
          className="card-integra w-full max-w-md p-8">
          {children}
        </motion.div>
      </main>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50"><MailCheck className="h-6 w-6 text-emeraude" /></span>
          <h1 className="mt-4 font-display text-xl font-extrabold text-slate-900">Vérifiez votre boîte mail</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            Si un compte existe avec l'adresse <strong>{email}</strong>, un email de réinitialisation vient d'être envoyé (lien valable 1 heure).
          </p>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-form">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-bleu-light"><KeyRound className="h-5 w-5 text-bleu" /></span>
          <h1 className="mt-4 font-display text-xl font-extrabold text-slate-900">Mot de passe oublié ?</h1>
          <p className="mt-1 text-sm text-slate-500">Indiquez votre email : nous vous enverrons un lien de réinitialisation.</p>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="exemple@email.com"
            className="mt-5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
            data-testid="forgot-email-input" />
          <button type="submit" disabled={loading || !email} className="btn-primary mt-4 w-full !py-3 disabled:opacity-50" data-testid="forgot-submit">
            {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Envoyer le lien de réinitialisation"}
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success(data.message);
      navigate("/login");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Lien invalide ou expiré.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <form onSubmit={submit} data-testid="reset-form">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-bleu-light"><KeyRound className="h-5 w-5 text-bleu" /></span>
        <h1 className="mt-4 font-display text-xl font-extrabold text-slate-900">Nouveau mot de passe</h1>
        <p className="mt-1 text-sm text-slate-500">Choisissez un nouveau mot de passe (8 caractères minimum).</p>
        {!token && <p className="mt-3 rounded-xl bg-rose-50 px-4 py-2.5 text-xs font-medium text-rouge" data-testid="reset-no-token">Lien invalide : le jeton de réinitialisation est manquant.</p>}
        <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Nouveau mot de passe"
          className="mt-5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
          data-testid="reset-password-input" />
        <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirmer le mot de passe"
          className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
          data-testid="reset-confirm-input" />
        <button type="submit" disabled={loading || !token || !password || !confirm} className="btn-primary mt-4 w-full !py-3 disabled:opacity-50" data-testid="reset-submit">
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Réinitialiser mon mot de passe"}
        </button>
      </form>
    </Shell>
  );
}
